import * as httpStatus from "http-status";

import axios from "axios";
import includes from "lodash/includes";
import objectToForm from "common/api/objectToForm";
import snakeize from "snakeize";

const camelcaseKeys = require("camelcase-keys");
const datePattern = [/^\d{4}-\d{2}-\d{2}$/, /^\d{2}-\d{2}-\d{4}$/, /^\d{4}\/\d{2}\/\d{2}$/, /^\d{2}\/\d{2}\/\d{4}$/];

// Helper function to deep merge original snake_case keys with camelCase keys
const addCamelCaseKeys = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    return obj.map(addCamelCaseKeys);
  }
  const camelCased = camelcaseKeys(obj, { deep: false, exclude: [...datePattern] });
  const result = { ...obj };
  for (const key in camelCased) {
    if (!(key in result)) {
      result[key] = camelCased[key];
    }
  }
  // Recursively process nested objects
  for (const key in result) {
    if (result[key] && typeof result[key] === "object") {
      result[key] = addCamelCaseKeys(result[key]);
    }
  }
  return result;
};

const api = axios.create({
  transformRequest: axios.defaults.transformRequest.concat((data) => {
    return data;
  }),
  transformResponse: axios.defaults.transformResponse.concat((data) => {
    return addCamelCaseKeys(data || {});
  }),
  validateStatus: (status) => {
    return status <= 401;
  },
});

const verbs = ["get", "post", "put", "patch", "delete"];

class ApiManager {}

function handleSuccess(res) {
  if (httpStatus.NO_CONTENT === res.status) {
    return {
      success: true,
    };
  }
  return {
    data: res.data,
    code: res.status,
  };
}

function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function handleFailed(error, url, verb, config, data, count) {
  const res = error.response;
  console.log("url fail", url, error);
  if (res) {
    return Promise.reject({
      data: res.data || {},
      code: res.status,
    });
  } else {
    if (count >= 10) {
      console.log(`${verb} ${url} ${error}`);
      return Promise.reject({
        data: error,
      });
    }
    await timeout(3000);
    if (!includes(["get", "delete"], verb))
      return await api[verb](url, data, config).then(handleSuccess, (res) =>
        handleFailed(res, url, verb, config, data, count + 1),
      );
    else
      return await api[verb](url, config).then(handleSuccess, (res) =>
        handleFailed(res, url, verb, config, {}, count + 1),
      );
  }
}

function preparePostFiles(data, files) {
  try {
    const formData = objectToForm(files);
    formData.append("payload", JSON.stringify(snakeize(data)));
    return formData;
  } catch (e) {
    return undefined;
  }
}

/* handle each verb here */
for (let verb of verbs) {
  if (!includes(["get", "delete"], verb)) {
    ApiManager.prototype[`${verb}WithFiles`] = function (url, data, files, config = {}) {
      const postData = preparePostFiles(data, files);
      config = Object.assign({ headers: { "Content-Type": "multipart/form-data" } }, config);
      if (config && config.params) config.params = snakeize(config.params);
      return api[verb](url, postData, config).then(handleSuccess, (res) =>
        handleFailed(res, url, verb, config, postData, 0),
      );
    };

    ApiManager.prototype[verb] = function (url, data, config) {
      if (config && config.params) config.params = snakeize(config.params);
      return api[verb](url, data, config).then(handleSuccess, (res) => handleFailed(res, url, verb, config, data, 0));
    };
  } else {
    ApiManager.prototype[verb] = function (url, config) {
      if (config && config.params) config.params = snakeize(config.params);
      return api[verb](url, config).then(handleSuccess, (res) => handleFailed(res, url, verb, config, {}, 0));
    };
  }
}

export default new ApiManager();
